import { Inject, Injectable } from '@angular/core';
import { API_PARAM_TYPE, ApiParams, IdentifierType } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, Audience, ErrorDCR, MODELS_STORE_NAME_TOKEN, PortfolioRetrievalParamsConfig } from '@ddt-web/config';
import { InjectConfig, PaConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { Country, OtherIdentifiers, PartnerPlatformControlsList, PortfolioRetrievalParams, WdsApiParamsBuilder, WebUserDataConstants } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { Config, StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import { PartnerConstants } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import { iif, Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators';

import { AppStoreConfig } from '../models';
import { AudienceService } from '../services/audience.service';
import { PortfolioTaggingService } from '../services/portfolio-tagging.service';
import { ApiService } from './api.service';
import { PreferenceData } from './models/preference.interface';

@UntilDestroy()
@Injectable()
export class DataUserDataService {
	public getAudience$: Observable<Audience>;

	@InjectConfig(SharedConfigKey.FLIP_MODELS_CONFIG_STORE_IDENTIFIER_TO_DAS_PORTFOLIO_ID) private readonly flipModelsConfigStoreIdentifierToDasPortfolioId: boolean = true;
	@InjectConfig(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID) public readonly shouldUseDasPortfolioId = true;
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) private readonly partnerPlatformControlsList: PartnerPlatformControlsList;
	@InjectConfig(PaConfigKey.SHOW_IMPORT_PARTNER_PORTFOLIO) private readonly showImportPartnerPortfolio: boolean;

	/**
	 * This service only deal with fetch and store data from /wds/partner-data/
	 */
	constructor(
		private apiService: ApiService,
		private storeService: StoreService,
		private audienceService: AudienceService,
		private taggingService: PortfolioTaggingService,
		private urlService: UrlService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		@Inject(MODELS_STORE_NAME_TOKEN) private modelsStoreName: string
	) {
		this.getAudience$ = this.audienceService.getAudience()
			.pipe(
				filter(audience => !!audience),
				distinctUntilChanged((previous, current) => previous['country'] !== current['country'])
			);
	}

	/**
	 * Retrieves portfolios specific to the user's audience
	 */
	public fetchUserDataForAudience<T>(
		param: any,
		ids: string[],
		config: Config<T>,
		includeChildren = true,
		rcs?: string,
		additionParams?: ApiParams[]
	): void {
		this.getAudience$.pipe(untilDestroyed(this)).subscribe(audience => {
			const audienceFilters = this.getCountryApiParamFilters(audience);

			// Separate additionalParams
			let childrenParam = [];
			let filterParams = [];
			if (additionParams) {
				childrenParam = additionParams.filter(param => param.paramId === 'includeChildren');
				filterParams = additionParams.filter(param => param.paramId === 'filter');
			}

			const wdsApiParamsBuilder = new WdsApiParamsBuilder();
			if (has(childrenParam, '[0]')) {
				wdsApiParamsBuilder.includeChildren = childrenParam[0];
			}
			wdsApiParamsBuilder.filters = [...audienceFilters, ...filterParams];
			const finalApiParams = wdsApiParamsBuilder.build();

			this.fetchUserDataWithConfig(param, ids, config, includeChildren, rcs, finalApiParams);
		});
	}

	private getCountryApiParamFilters(audience: Audience): ApiParams[] {
		// Set country filter
		const country = audience.country;
		let countryFilter;
		if (country && ![Country.default, Country.en_US].includes(country)) {
			countryFilter = { paramId: 'filter', value: 'country_s:' + country, type: API_PARAM_TYPE.IDENTIFIER.PARAM };
		}

		return [countryFilter].filter(apiParam => !!apiParam);
	}

	/**
	 * Function to call ApiService
	 */
	private fetchUserDataWithConfig<T>(
		param: any,
		ids: string[],
		config: Config<T>,
		includeChildren = true,
		rcs?: string,
		additionParams?: ApiParams[]
	): void {
		this.apiService.fetchUserData(param, this.createUserDataParamWithId(ids, includeChildren, additionParams), rcs).subscribe(responseJson => {
			const response = this.getUserDataResponse(responseJson);
			this.updateStoreFromResponse([config], response, ids, responseJson);
		});
	}

	/**
 * Retrieves engagements without needing any additional audience filter logic
 */
	public fetchUserApbEngagementWithConfig<T>(
		param: any,
		ids: string[],
		config: Config<T>
	): void {
		this.fetchUserDataWithConfig(param, ids, config);
	}

	public getPortfoliosById<T>(
		ids: string[],
		apiParamOptions: PortfolioRetrievalParams = PortfolioRetrievalParamsConfig.DEFAULT
	): Observable<{ ids: string[]; portfolios: {}[]; responseJson: any }> {
		const modelsInfo$ = this.storeService.selectAll(this.storeConfig[this.modelsStoreName]).pipe(
			map(config => (!config.isLoading && config.entities) ? config.entities : new Map()));
		const fetchACSPortfolioData$ = this.apiService.fetchACSPortfolioData(ids.map(id => PortfolioIdUtil.ALADDIN_PORTFOLIO_CODE_PREFIX + id), apiParamOptions); // mapping ids to add prefix APC before each id in ids array

		const mapDasIdToAladdinCodeAndThenFetchACSPortfolioData$ = modelsInfo$.pipe(
			map((modelData) => ids.map(id => PortfolioIdUtil.isBlackRockModel(id) ? PortfolioIdUtil.ALADDIN_PORTFOLIO_CODE_PREFIX + modelData[id]?.aladdinPortfolioCode : id))
		).pipe(
			switchMap(portfolioIds => this.apiService.fetchACSPortfolioData(portfolioIds, apiParamOptions).pipe(
				map((response) => {
					const resultList = response['resultList'].map((item, index) => {
						const response = Object.assign({}, { ...item, dasId: ids[index] });
						return response;
					});

					return { ...response, resultList };
				})
			))
		);
		return iif(() => this.flipModelsConfigStoreIdentifierToDasPortfolioId, mapDasIdToAladdinCodeAndThenFetchACSPortfolioData$, fetchACSPortfolioData$).pipe(
			map((response) => {
				const portfolioResponse = response['resultList'];
				const transformedResponse = this.flattenAllDataResponse(portfolioResponse, get(response, 'errorCodes', {}));
				const responseJson = Object.assign({}, response, { resultList: transformedResponse });
				return { responseJson, portfolios: transformedResponse };
			}),
			map(({ portfolios, responseJson }) => {
				// check if ids can be mapped from the response, otherwise use the ones from the method params
				// NOTE: if ids is empty, store will not be updated for the selected ids, which will cause an infinite loop in ST and Tax

				//need this for blending
				const transformedResponseIds = portfolios.map(portfolio => get(portfolio, 'id'));
				ids = isEmpty(transformedResponseIds) ? ids : transformedResponseIds;

				return { ids, portfolios, responseJson };
			})
		);

	}

	/**
	 * Function to call ApiService
	 */
	public fetchPortfolioWithId<T>(
		ids: string[],
		config: Config<T>,
		apiParamOptions: PortfolioRetrievalParams = PortfolioRetrievalParamsConfig.DEFAULT
	): void {
		this.getPortfoliosById<T>(ids, apiParamOptions).pipe(first()).subscribe(
			({ ids, portfolios, responseJson }) => {
				this.updateStoreFromResponse([config], portfolios, ids, responseJson);
			},
			error => {
				this.storeService.upsertMany(config, []);
				throw error;
			}
		);
	}

	public flattenAllDataResponse(portfolioResponse: Array<Object>, errorPortfolios: { [portfolioId: string]: any } = {}): Array<{}> {
		const portfolios = [];

		portfolioResponse.filter(portfolio => !!portfolio).forEach(portfolio => {
			if (has(portfolio, 'response')) {
				portfolio = portfolio['response'];
			}

			const children = this.processPortfolioChildren(portfolio);

			const enrichedPortfolio = Object.assign({}, get(portfolio, 'allData', {}), portfolio, { children },
				get(portfolio, 'portfolio', {}), get(portfolio, 'portfolio.allData', {}));

			if (PortfolioIdUtil.isThirdPartyModel(portfolio['allData']?.['requestModelId']) && !enrichedPortfolio.tpmId) {
				enrichedPortfolio.tpmId = enrichedPortfolio.id;
				enrichedPortfolio.id = portfolio['allData']?.['requestModelId'];
				enrichedPortfolio.allData.id = portfolio['allData']?.['requestModelId'];
			}

			if (enrichedPortfolio.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO) {
				enrichedPortfolio.id = PartnerConstants.PARTNER.EXT_IDENTIFIER + enrichedPortfolio.id;
			}

			// Blk models - Using dasId and aladdinPortfolioCode is not present at top level
			if (PortfolioIdUtil.isBlackRockModel(portfolio['id']) && this.shouldUseDasPortfolioId && (!portfolio['aladdinPortfolioCode'])) {
				// set aladdinPortfolioCode to id resolved via ACServer (the aladdin portfolio code)
				enrichedPortfolio.aladdinPortfolioCode = portfolio['id'];
				enrichedPortfolio.allData.aladdinPortfolioCode = portfolio['id'];
				// To remove form requested model id APC@ prefix
				enrichedPortfolio.requestModelId = PortfolioIdUtil.getAladdinPortfolioCode(portfolio['allData']?.['requestModelId']);
				enrichedPortfolio.allData.requestModelId = PortfolioIdUtil.getAladdinPortfolioCode(portfolio['allData']?.['requestModelId']);

				// set the id returned by ACServer to the dasId to keep consistency
				// we want to use the dasId in FE state, and APC only for ACServer and PAS calls
				// Fallback to allData dasId OR requestedModelId to support old blends and deep links
				enrichedPortfolio.id = portfolio['dasId'] || portfolio['allData']?.['dasId'] || portfolio['allData']?.['requestModelId'];
				enrichedPortfolio.allData.id = portfolio['dasId'] || portfolio['allData']?.['dasId'] || portfolio['allData']?.['requestModelId'];
			}

			portfolios.push(enrichedPortfolio);

			const nestedPortfolios = get(enrichedPortfolio, 'children', []).filter(child => child.portfolio).map(child => child.portfolio);
			if (!isEmpty(nestedPortfolios)) {
				portfolios.push(...this.flattenAllDataResponse(nestedPortfolios));
			}

			const models = get(enrichedPortfolio, 'children', []).filter(child => {
				const identifier = get(child, 'identifier') || get(child, 'id');
				const isModelAlreadyProcessed = portfolios.find(portfolio => identifier === (get(portfolio, 'identifier') || get(portfolio, 'id')));
				return PortfolioIdUtil.isModel(identifier) && !isModelAlreadyProcessed;
			});
			if (!isEmpty(models)) {
				portfolios.push(...this.flattenAllDataResponse(models));
			}
		});

		Object.keys(errorPortfolios).forEach(errorPortfolioId => {
			portfolios.push(Object.assign({ id: errorPortfolioId }, errorPortfolios[errorPortfolioId]));
		});

		return portfolios;
	}

	/**
	 * Method to store user preference records.
	 * @param {Config[]} configs list of configs to update from the response
	 * @param {string} preferenceKey the preference key to use for saving the record
	 * @param {any} preferenceData preference data object to be saved
	 */
	public setUserDataPreference<T>(configs: Config<T>[], preferenceKey: string, preferenceData: any, removeDynamicFieldSuffix: boolean = true): void {
		// Enrich preferenceData with tags
		preferenceData = Object.assign({}, this.taggingService.setAnalyticsTags({}), preferenceData);
		this.apiService.setPreference(preferenceKey, preferenceData, removeDynamicFieldSuffix).subscribe(
			responseJson => {
				const response = this.getUserDataPreferenceResponse(responseJson);
				this.updateStoreFromPreferenceResponse(configs, response, Object.keys(preferenceData));
			},
			errorResponseJson => this.handlePreferenceResponseError(errorResponseJson)
		);
	}

	/**
	 * Method to fetch user preference records.
	 * @param {Config[]} configs list of configs to update from the response
	 * @param {string} preferenceKey the preference key to use in fetching the record
	 * @param {string[]} properties the list of attributes to update the store with from the response
	 */
	public fetchUserDataPreference<T>(
		configs: Config<T>[],
		preferenceKey: string,
		properties: string[],
		removeDynamicFieldSuffix: boolean = true,
		defaultValues: { [key: string]: any } = {}
	): void {
		this.apiService.fetchPreference(preferenceKey, removeDynamicFieldSuffix).subscribe(
			responseJson => {
				if (responseJson) {
					// Set defaults for unset preferences properties
					for (const key in defaultValues) {
						if (!responseJson.hasOwnProperty(key)) {
							responseJson[key] = defaultValues[key];
						}
					}
				} else {
					responseJson = defaultValues as PreferenceData;
				}
				this.updateStoreFromPreferenceResponse(configs, responseJson, properties);
			},
			errorResponseJson => this.handlePreferenceResponseError(errorResponseJson)
		);
	}

	public handlePreferenceResponseError(errorResponseJson: any): void {
		if (errorResponseJson.errors) {
			throw new Error('Error while uploading/downloading a record from wtp - ' + errorResponseJson);
		}
	}

	private createErrorObject(id: string): object {
		return {
			// This needs significantly more work
			success: false,
			id: id,
			error: `Object with id: ${id} not found.`
		};
	}

	public updateStoreFromPreferenceResponse<T>(configs: Config<T>[], response: any, properties: string[]): void {
		const storeUpdate = {};
		properties.forEach(property => (storeUpdate[property] = get(response, property, null)));
		configs.forEach(config => this.storeService.upsertOne(config, storeUpdate));
	}

	/**
	 * Add or Update store with response
	 */
	public updateStoreFromResponse(configs: any[], response: any[], ids: string[], responseJson?: any, shouldUseRequestModelId: boolean = false): void {
		if (ids) {
			configs.forEach(config => {
				this.storeService.upsertMany(
					config,
					ids.map(id => {
						id = String(id);
						const res = response.find(obj => {
							const otherIdentifiers: OtherIdentifiers[] = get(obj, 'otherIdentifiers');
							if (otherIdentifiers) {
								const idObj = otherIdentifiers.find(identifier => identifier['identifierType'] === 'CUSTOM');
								if (idObj && id === PartnerConstants.PARTNER.PARTNER_IDENTIFIER + idObj['identifier']) {
									return obj;
								}
							}
							if (id === PartnerConstants.PARTNER.EXT_IDENTIFIER + obj.uniqueId) {
								return obj;
							}
							if (shouldUseRequestModelId) {
								return id === obj?.allData?.requestModelId;
							}
							return id === String(obj.portfolioId || obj.id || obj.uniqueId);
						});
						if (res) {
							return Object.assign({ ...res }, { id: id });
						} else {
							// create error obj;
							return this.createErrorObject(id);
						}
					})
				);
				// throw error after update store, so components still render when not all data returned
				this.handleResponseError(responseJson, config.name);
			});
		} else {
			configs.forEach(config => {
				this.storeService.upsertMany(
					config,
					response.map(userPortfolio =>
						Object.assign(
							{ ...userPortfolio },
							{
								id: userPortfolio.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO ?
									PartnerConstants.PARTNER.EXT_IDENTIFIER + userPortfolio.uniqueId : userPortfolio.uniqueId
							}
						)
					)
				);
				// throw error after update store, so components still render when not all data returned
				this.handleResponseError(responseJson, config.name);
			});
		}
	}


	/**
	 * Handle the response from /wds/partner-data/
	 */
	public getUserDataResponse(responseJson: any): any[] {
		return get(responseJson, 'resultMap.SEARCH_RESULTS[0].resultList', []);
	}

	public getUserDataPreferenceResponse(responseJson: any): any {
		return get(responseJson, 'resultMap.SEARCH_EDIT_RESULTS[0].resultList[0]', {});
	}

	/**
	 * throw error to be handled by application handler while responseJson:
	 * success = false OR
	 * success = true, allDataReturned = false OR
	 *
	 * incase of an empty result in fetching engagement data -
	 * configName = engagementPortfolios && resultList is empty
	 */
	public handleResponseError(responseJson: any, configName: string): void {
		this.handleExternalPortfolioDeeplinkError(responseJson);
		if (responseJson && ((configName === 'associatedPortfolios' && !responseJson.success) ||
			(configName !== 'associatedPortfolios' && !(responseJson.success && responseJson.allDataReturned)))) {
			throw new ErrorObject(responseJson, configName);
		} else if (configName === 'engagementPortfolios' && !responseJson?.resultMap?.SEARCH_RESULTS?.[0]?.resultList?.length) {
			const errorObj = {
				action: 'REDIRECT_TO_NEW_ANALYSIS',
				success: false,
				error: 'Engagement data not found'
			};
			throw new ErrorObject(errorObj, configName);
		}
	}
	/**
	 * Throw error for external portfolios in any of these cases:
	 * 1. Portfolios are from unauthorized platform
	 * 2. Url does not contain EXT@ prefix for partner ports
	 * 3. More than one partner platform portfolios are being imported (Orion AND Black Diamond)
	 */
	public handleExternalPortfolioDeeplinkError(response: any): void {
		const urlIds = this.urlService.getPortfolioIds();
		const externalPortfolios = response?.resultList?.filter(result => result.subType === WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO) ?? [];
		if (externalPortfolios.length && urlIds) {
			// TODO chmalcol: Confirm what error dialog should display for all of these
			externalPortfolios.forEach(port => {
				// Throw error if any of the portfolios are from a partner that is unauthorized
				if (!this.showImportPartnerPortfolio || !this.partnerPlatformControlsList?.[port.originPlatform.platform]?.showSavedPortfolios) {
					throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
				}
				// Throw error if the urlId matches the uniqueId as this means they are trying to access the portfolio directly
				if (urlIds.includes(port.uniqueId)) {
					throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
				}
			});
			const noOfPartnerTypesWithinPortfolios: number = new Set(externalPortfolios.map(portfolio => portfolio.originPlatform.platform)).size;
			// Throw error if user tries to import multiple different partner platform portfolios
			if (noOfPartnerTypesWithinPortfolios > 1) {
				throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
			}
		}
	}

	/**
	 * Create and return user data params for "/wds/partner-data/" based on the provided ids.
	 * The user data params are:
	 * - uniqueIds: the provided ids joined by ','
	 * - sort: 'portOrder_i asc', which preserves the order of the portfolios within engagement
	 * 			using the portOrder param set within APB
	 */
	public createUserDataParamWithId(ids: string[], includeChildren = true, additionParams?: ApiParams[]): any {
		const params = {
			includeChildren: includeChildren,
			includeAllowedPermsForCurrentUser: 'true'
		};
		if (additionParams) {
			additionParams.forEach(param => {
				params[param.paramId] = param.value;
			});
		}

		if (ids && ids.length) {
			params['uniqueIds'] = ids.join(',');
			params['sort'] = 'portOrder_i asc';
		}

		return params;
	}

	private processPortfolioChildren(portfolio): any {
		let children = get(portfolio, 'children', []).map(child => Object.assign({}, child, get(child, 'allData', {})));

		// if model via PAS, different enrichment required 
		if (PortfolioIdUtil.isModel(portfolio['id']) || PortfolioIdUtil.isModel(portfolio['allData']?.['requestModelId'])) {
			children = children.map(child => this.enrichModelChild(child));
		} else {
			children = this.enrichSecurityChildren(children, !!portfolio['allData']?.['baseModel']);
		}

		return children;
	}
	/**
	 * Enriches security children to have valid identifier & identifierType.
	 * If child has identifierType as 'SECURITY_ID', it identifies the valid identifier from the IDENTIFIER_TO_IDENTIFIER_TYPE_MAPPING
	 * and it is using it to update the identifier & identifierType accordingly.
	 *
	 * If provided children belong to customised model, the enrichment also sets cusip based on the identifier (ticker@cusip@isin)
	 * with custom logic for old cash securities.
	 *
	 * @param children
	 * @returns enriched children
	 */
	private enrichSecurityChildren(children: any, isCustomisedModel: boolean): any {
		return children.map(child => {

			// This is for partner - to enrich
			// cash cusip returned from iretire server is incorrect so skipping it, remove check once fixed
			child.otherIdentifiers?.forEach(otherIdentifier => {
				if (otherIdentifier.identifierType !== IdentifierType.CUSTOM && !(otherIdentifier.identifier === 'CASH-USD' && otherIdentifier.identifierType === 'CUSIP')) {
					child[otherIdentifier.identifierType.toLowerCase()] = otherIdentifier.identifier;
				}
			});

			// if identifier type is a security identifier we enrich metadata
			if (WebUserDataConstants.SECURITY_IDENTIFIERS.includes(child.identifierType)) {
				// old customised models did not save any identifiers except identifier which had the structure of ticker@cusip@isin so we need to enrich from it
				const identifiers = child.identifier?.split('@');
				const ticker = identifiers[0];
				const cusip = identifiers[1];
				const isin = identifiers[2];

				child = {
					...child,
					cusip: child.cusip ?? cusip,
					ticker: child.ticker ?? ticker,
					isin: child.isin ?? isin
				};

			} else if (!!child.portfolio) {
				if (PortfolioIdUtil.isThirdPartyModel(child.identifier)) {
					const tpmId = child.portfolio.id;
					child.portfolio.id = child.identifier;
					child.portfolio.allData.id = child.identifier;
					child.tpmId = tpmId;
					child.allData.tpmId = tpmId;
					child.portfolio.tpmId = tpmId;
					child.portfolio.allData.tpmId = tpmId;
				}

				if (PortfolioIdUtil.isBlackRockModel(child.portfolio.id)) {
					// updating aladdinPortfolioCode to the one resolved via ACServer (the APC)
					child.aladdinPortfolioCode = child.portfolio.id;
					child.allData.aladdinPortfolioCode = child.portfolio.id;

					// now set the child id with the dasId which ACServer should return
					// we want to use the dasId in FE state, and APC only for ACServer and PAS calls
					child.id = child.portfolio.dasId || child.portfolio['allData']?.['dasId'] || child.portfolio['allData']?.['requestModelId'];
					child.allData.id = child.portfolio.dasId || child.portfolio['allData']?.['dasId'] || child.portfolio['allData']?.['requestModelId'];
				}

				// update recursively portfolios/models within blend
				const portfolioChildren = this.processPortfolioChildren(child.portfolio);
				child.portfolio = { ...child.portfolio, children: portfolioChildren };
			}

			return child;
		});
	}

	/**
	 * Enriches model children to have identifiers provided as part of posInfo at the child level.
	 * This is required to have consistent path across portfolio children and model children.
	 *
	 * @param child
	 * @returns enriched model child
	 */
	private enrichModelChild(child: any): any {
		const posInfoUpdate = {};
		Object.entries(child.posInfo).forEach(([posId, posValue]) => {
			posInfoUpdate[posId] = get(posValue, 'stringValue');
		});

		child = { ...child, ...posInfoUpdate };

		// translate identifiers to be correctly mapped (AC Server returns all children with BCUSIP identifierType)
		const identifierAndIdentifierType = HoldingsUtil.generateIdentifierAndIdentifierType(child);
		child.identifier = identifierAndIdentifierType?.identifier;
		child.identifierType = identifierAndIdentifierType?.identifierType;

		return child;
	}

}
